import React from "react";
import { NavLink, useNavigate } from "react-router-dom";

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";

function useValidAction(action) {
  const navigate = useNavigate();
  return React.useMemo(() => {
    if (!action) return null;
    if (React.isValidElement(action)) return action;
    const { to, href, onClick, label, icon: Icon, variant = "default", size: btnSize = "sm", disabled } = action;
    const handle = (e) => {
      if (disabled) return;
      if (typeof onClick === "function") {
        const r = onClick(e);
        if (typeof r?.then === "function") return r;
        return r;
      }
      if (to && !e.defaultPrevented) {
        e.preventDefault();
        navigate(to);
      }
    };
    const conteudo = (
      <>
        {Icon && <Icon className="h-4 w-4" />}
        {label}
      </>
    );
    if (to) {
      return (
        <NavLink to={to} className="inline-flex">
          {({ isActive }) => (
            <Button
              asChild={false}
              variant={variant}
              size={btnSize}
              disabled={disabled || isActive}
              onClick={handle}
            >
              {conteudo}
            </Button>
          )}
        </NavLink>
      );
    }
    if (href) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          className="inline-flex"
          onClick={handle}
        >
          <Button asChild={false} variant={variant} size={btnSize} disabled={disabled}>
            {conteudo}
          </Button>
        </a>
      );
    }
    return (
      <Button
        asChild={false}
        variant={variant}
        size={btnSize}
        disabled={disabled}
        onClick={handle}
      >
        {conteudo}
      </Button>
    );
  }, [action, navigate]);
}

function iconBgClassName(intent) {
  switch (intent) {
    case "primary":
      return "bg-primary/10 text-primary";
    case "success":
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
    case "warning":
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400";
    case "danger":
      return "bg-destructive/10 text-destructive";
    case "muted":
    default:
      return "bg-muted text-muted-foreground";
  }
}

export function EmptyStateCard({
  icon: Icon,
  title,
  subtitle,
  action,
  secondaryAction,
  variant = "flex",
  size = "md",
  intent = "muted",
  className,
  children,
  titleClassName,
  subtitleClassName,
  iconWrapperClassName,
}) {
  const botaoPrimario = useValidAction(action);
  const botaoSecundario = useValidAction(secondaryAction);

  const iconSize =
    size === "lg" ? "h-14 w-14" : size === "sm" ? "h-8 w-8" : "h-11 w-11";
  const iconInner =
    size === "lg" ? "h-7 w-7" : size === "sm" ? "h-4 w-4" : "h-5 w-5";
  const titleSize =
    size === "lg"
      ? "text-base font-semibold"
      : size === "sm"
        ? "text-sm font-medium"
        : "text-sm font-medium";
  const subtitleSize =
    size === "lg"
      ? "text-sm leading-relaxed"
      : size === "sm"
        ? "text-[11px] leading-relaxed"
        : "text-xs leading-relaxed";

  const corpo = (
    <>
      {Icon && (
        <div
          className={cn(
            "mb-2 inline-flex items-center justify-center rounded-full",
            iconSize,
            iconBgClassName(intent),
            iconWrapperClassName
          )}
        >
          <Icon className={iconInner} aria-hidden />
        </div>
      )}
      {title && (
        <p className={cn(titleSize, "text-foreground", titleClassName)}>
          {title}
        </p>
      )}
      {subtitle && (
        <p
          className={cn(
            subtitleSize,
            "text-muted-foreground",
            "max-w-md",
            subtitleClassName
          )}
        >
          {subtitle}
        </p>
      )}
      {children}
      {(botaoPrimario || botaoSecundario) && (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
          {botaoPrimario}
          {botaoSecundario}
        </div>
      )}
    </>
  );

  if (variant === "tableCell") {
    return (
      <div
        className={cn(
          "mx-auto flex w-full flex-col items-center justify-center gap-1.5 px-6 py-10 text-center",
          className
        )}
      >
        {corpo}
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div
        className={cn(
          "flex flex-col items-start gap-1.5 text-left",
          className
        )}
      >
        {corpo}
      </div>
    );
  }

  // variant === "flex" (default)
  return (
    <div
      className={cn(
        "flex h-full min-h-[180px] w-full flex-col items-center justify-center gap-1.5 px-8 py-12 text-center",
        className
      )}
    >
      {corpo}
    </div>
  );
}

export default EmptyStateCard;
